import { SocialAuthAttemptSchema } from './schemas';
import type { SocialAuthAttempt, SocialProvider } from './types';

const STORAGE_KEY = 'moveris.social-auth-attempt';

function randomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

function toBase64Url(bytes: Uint8Array): string {
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Starts a social login attempt: creates a random `state` and a PKCE code verifier, stores both with the provider in sessionStorage (which survives the round trip in the same tab), and returns the `state` and the S256 code challenge for the provider's authorize URL.
 */
export async function createSocialAuthAttempt(
  provider: SocialProvider,
): Promise<{ state: string; codeChallenge: string }> {
  const state = Array.from(randomBytes(32), (byte) => byte.toString(16).padStart(2, '0')).join('');
  const codeVerifier = toBase64Url(randomBytes(32));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  const attempt: SocialAuthAttempt = { state, provider, codeVerifier };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempt));
  return { state, codeChallenge: toBase64Url(new Uint8Array(digest)) };
}

/**
 * Ends the stored attempt and returns it when `state` matches it exactly, or `null` when there is no attempt or it does not match. The attempt is removed either way, so it can be used once.
 */
export function consumeSocialAuthAttempt(state: string | null): SocialAuthAttempt | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  if (!raw || !state) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  const attempt = SocialAuthAttemptSchema.safeParse(parsed);
  if (!attempt.success || attempt.data.state !== state) return null;
  return attempt.data;
}
